'use client'

import React, { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight, BookOpen, FileText, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import type { Media } from '@/payload-types'
import { RichText } from '../RichText'
import { getMediaUrl } from '../../lib/media'

type ResourceType = 'visa-guide' | 'article'

export interface Resource {
  id: number
  slug: string
  title: string
  description?: string | null
  featuredImage?: Media | null
  publishedDate?: string | null
  type: ResourceType
  author?: {
    name: string
    photo?: Media | null
  } | null
}

interface ResourceListCarouselProps {
  resources: Resource[]
  blogHeadingRichText?: any
  blogHeadingString?: string
  showHeader?: boolean
  /** Remove bottom padding — homepage only */
  compactBottom?: boolean
}

const resourceTypeConfig: Record<
  ResourceType,
  { label: string; icon: React.ElementType; bgColor: string }
> = {
  'visa-guide': {
    label: 'Visa Guide',
    icon: BookOpen,
    bgColor: 'bg-brand-gold',
  },
  article: {
    label: 'Article',
    icon: FileText,
    bgColor: 'bg-brand-blue',
  },
}

export function ResourceListCarousel({
  resources,
  blogHeadingRichText,
  blogHeadingString,
  showHeader = true,
  compactBottom = false,
}: ResourceListCarouselProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isAtEnd, setIsAtEnd] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const filteredResources = searchQuery
    ? resources.filter(
        (resource) =>
          resource.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          resource.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : resources

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      // Check if scrolled to the end (with 5px tolerance)
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 5)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      // Check initial position
      checkScrollPosition()
      // Add scroll listener
      container.addEventListener('scroll', checkScrollPosition)
      return () => container.removeEventListener('scroll', checkScrollPosition)
    }
  }, [filteredResources])

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' })
    }
  }

  return (
    <section
      className={`py-12 md:py-24 px-6 md:px-12 ${compactBottom ? 'pb-0!' : ''}`}
    >
      <div className="max-w-380 mx-auto">
        {/* Header with Search */}
        {showHeader && (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8 md:mb-12">
            {blogHeadingRichText && (
              <div className="space-y-2">
                <RichText
                  data={blogHeadingRichText}
                  className="text-3xl md:text-4xl leading-tight font-medium text-gray-900 [&_span.font-serif]:text-brand-gold [&_span.font-serif]:italic"
                />
              </div>
            )}

            {blogHeadingString && (
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900">
                  {blogHeadingString}
                </h2>
              </div>
            )}

            {/* Search Bar */}
            <div className="relative w-full md:w-auto md:min-w-[320px]">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Search className="w-4 h-4 text-white" />
                </div>
              </div>
              <input
                type="text"
                placeholder="Search resources"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-4 py-3 rounded-full border border-gray-300 bg-white text-gray-900 placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Carousel */}
        <div className="relative">
          {/* Scrollable container wrapper */}
          <div className="relative">
            {/* Fade gradient overlay on the right - only covers the cards */}
            <div
              className={`absolute right-0 top-0 bottom-4 w-12 md:w-16 pointer-events-none z-10 transition-opacity duration-300 ${
                isAtEnd ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                background: 'linear-gradient(to left, rgba(245, 245, 240, 0.7), transparent)',
              }}
            />

            <div
              ref={scrollContainerRef}
              className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filteredResources.map((resource) => {
                const imageUrl = getMediaUrl(resource.featuredImage, 'card')
                const typeConfig = resourceTypeConfig[resource.type]
                const Icon = typeConfig.icon
                const href =
                  resource.type === 'article'
                    ? `/articles/${resource.slug}`
                    : `/guides/${resource.slug}`

                return (
                  <Link
                    key={`${resource.type}-${resource.id}`}
                    href={href}
                    className="shrink-0 w-[260px] md:w-[280px] group relative bg-gray-900 rounded-2xl overflow-hidden aspect-3/4 flex flex-col snap-start"
                  >
                    {/* Background Image */}
                    {imageUrl && (
                      <div className="absolute inset-0">
                        <Image
                          src={imageUrl}
                          alt={resource.title || ''}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-linear-to-t from-gray-900/90 via-gray-900/40 to-transparent" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="relative z-10 flex flex-col h-full p-4 md:p-5">
                      {/* Type Badge */}
                      <div
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white ${typeConfig.bgColor} w-fit`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {typeConfig.label}
                      </div>

                      {/* Spacer */}
                      <div className="flex-1" />

                      {/* Title & Description */}
                      <div className="space-y-2">
                        <h3
                          className="text-lg md:text-2xl font-serif text-white leading-tight"
                          style={{
                            textShadow: '0px 0px 12px rgba(0,0,0,0.5)',
                          }}
                        >
                          {resource.title}
                        </h3>
                        {resource.description && (
                          <p
                            className="text-sm text-white/80 line-clamp-3"
                            style={{
                              textShadow: '0px 0px 8px rgba(0,0,0,0.4)',
                            }}
                          >
                            {resource.description}
                          </p>
                        )}
                      </div>

                      {/* Author */}
                      {resource.author && (
                        <div className="flex items-center gap-2 mt-3">
                          {getMediaUrl(resource.author.photo, 'thumbnail') && (
                            <div className="relative w-6 h-6 shrink-0 aspect-square rounded-full overflow-hidden">
                              <Image
                                src={getMediaUrl(resource.author.photo, 'thumbnail')}
                                alt={resource.author.name}
                                fill
                                sizes="24px"
                                className="object-cover"
                              />
                            </div>
                          )}
                          <span className="text-xs text-white/80">{resource.author.name}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="mt-6 w-full flex justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={scrollLeft}
                className="w-10 h-10 rounded-full border border-primary hover:border-primary hover:bg-primary hover:text-white flex items-center justify-center transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={scrollRight}
                className="w-10 h-10 rounded-full border border-primary hover:border-primary hover:bg-primary hover:text-white flex items-center justify-center transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            {/* View All Articles Button */}
            {showHeader && (
              <div className="flex items-center">
                <Link
                  href="/articles"
                  className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm md:text-base"
                >
                  View All News & Articles
                  <ArrowUpRight className="w-4 md:w-5 h-4 md:h-5" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* No results message */}
        {filteredResources.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No resources match your search.</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  )
}
